/**
 * Luqta Web SDK
 *
 * Official JavaScript/TypeScript SDK for integrating Luqta contest management,
 * user engagement, and gamification features into web applications.
 *
 * Supports two modes:
 * 1. Custom UI Mode - API-only integration for custom UI implementations
 * 2. Pre-configured UI Mode - Complete UI with branding customization
 *
 * And two API access types:
 * 1. Application APIs - Only require API Key + App ID
 * 2. End-User APIs - Require API Key + App ID + User identification
 */

import { LuqtaClient } from './client';
import {
  LuqtaConfig,
  CustomModeConfig,
  PreconfiguredModeConfig,
  LegacyConfig,
  RequestOptions,
  ApiResponse,
  PaginatedResponse,
  LuqtaError,
  UserProfile,
  UserIdentification,
  BrandingConfig,
  UIConfig,
  SDKMode,
  Contest,
  ContestBanner,
  ParticipateProgress,
  Level,
  LevelImage,
  LevelCompleteRequest,
  Quiz,
  QuizQuestion,
  QuizOption,
  QuizAttempt,
  QuizProgress,
  QuizResult,
  Prize,
  Reward,
  UserEarnings,
  Notification,
  UIAction,
  APPLICATION_ENDPOINTS,
  USER_ENDPOINTS,
  ApplicationEndpoint,
  UserEndpoint,
} from './types';

/**
 * Creates a new Luqta SDK client instance
 *
 * @example Custom Mode (API-only):
 * ```typescript
 * const client = createClient({
 *   mode: 'custom',
 *   apiKey: 'your-api-key',
 *   appId: 'your-app-id',
 *   user: { email: 'user@example.com' }
 * });
 * ```
 *
 * @example Pre-configured Mode:
 * ```typescript
 * const client = createClient({
 *   mode: 'preconfigured',
 *   apiKey: 'your-api-key',
 *   appId: 'your-app-id',
 *   containerId: 'luqta-container',
 *   branding: { primaryColor: '#5304fb' }
 * });
 * ```
 */
export function createClient(config: LuqtaConfig | LegacyConfig): LuqtaClient {
  return new LuqtaClient(config);
}

// Export main client class
export { LuqtaClient };

// Export error class
export { LuqtaError };

// Export configuration types
export type {
  LuqtaConfig,
  CustomModeConfig,
  PreconfiguredModeConfig,
  LegacyConfig,
  BrandingConfig,
  UIConfig,
  SDKMode,
};

// Export request/response types
export type {
  RequestOptions,
  ApiResponse,
  PaginatedResponse,
};

// Export user types
export type {
  UserProfile,
  UserIdentification,
};

// Export entity types
export type {
  Contest,
  ContestBanner,
  ParticipateProgress,
  Level,
  LevelImage,
  LevelCompleteRequest,
  Quiz,
  QuizQuestion,
  QuizOption,
  QuizAttempt,
  QuizProgress,
  QuizResult,
  Prize,
  Reward,
  UserEarnings,
  Notification,
  UIAction,
};

// Export endpoint constants
export {
  APPLICATION_ENDPOINTS,
  USER_ENDPOINTS,
};

export type {
  ApplicationEndpoint,
  UserEndpoint,
};

// Export UI utilities (for advanced customization)
export { LuqtaUIRenderer } from './ui/renderer';
export { ICONS, getLevelTypeIcon, getLevelTypeName } from './ui/icons';
export { injectStyles, generateCSSVariables, SDK_STYLES, DEFAULT_BRANDING } from './ui/styles';

// Default export for easier usage
export default {
  createClient,
  LuqtaClient,
  LuqtaError,
  APPLICATION_ENDPOINTS,
  USER_ENDPOINTS,
};
